import React, { useState } from 'react';
import { MessageSquare, Volume2, Type, CheckCircle, XCircle, Download, Copy } from 'lucide-react';
import { ProductDesignPageHeader, ProductDesignSection } from './priorities-ai-design/ProductDesignDocPrimitives';

export const ToneVoiceSystem: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'voice' | 'tone' | 'vocabulary' | 'microcopy'>('voice');

  const VoiceAttribute: React.FC<{
    letter: string;
    title: string;
    description: string;
    example: string;
    color: string;
  }> = ({ letter, title, description, example, color }) => (
    <div className="border border-[var(--rule)] bg-[var(--surface)] p-6">
      <div className="flex items-center gap-4 mb-4">
        <div className={`w-12 h-12 ${color} rounded-full flex items-center justify-center text-white font-heading font-semibold text-xl`}>
          {letter}
        </div>
        <h3 className="text-xl font-heading font-semibold text-[var(--text-pri)]">{title}</h3>
      </div>
      <p className="text-[var(--text-sec)] mb-4">{description}</p>
      <div className="bg-[var(--surface-raised)] p-4">
        <p className="text-sm text-[var(--text-sec)] italic">"{example}"</p>
      </div>
    </div>
  );

  const ToneContext: React.FC<{
    title: string;
    when: string;
    tone: string;
    example: string;
    color: string;
  }> = ({ title, when, tone, example, color }) => (
    <div className="border border-[var(--rule)] bg-[var(--surface)] p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-3 h-3 ${color} rounded-full`}></div>
        <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)]">{title}</h3>
      </div>
      <div className="space-y-3">
        <div>
          <p className="text-sm font-semibold text-[var(--text-sec)] mb-1">When:</p>
          <p className="text-sm text-[var(--text-sec)]">{when}</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-[var(--text-sec)] mb-1">Tone:</p>
          <p className="text-sm text-[var(--text-sec)]">{tone}</p>
        </div>
        <div className="bg-[var(--surface-raised)] p-3">
          <p className="text-sm text-[var(--text-sec)] italic">"{example}"</p>
        </div>
      </div>
    </div>
  );

  const VocabularyItem: React.FC<{
    term: string;
    replacement: string;
    reason: string;
    isPositive: boolean;
  }> = ({ term, replacement, reason, isPositive }) => (
    <div className={` p-4 border ${isPositive ? 'bg-[var(--green-dim)] border-[var(--rule)]' : 'bg-[var(--mustard-dim)] border-[var(--rule)]'}`}>
      <div className="flex items-center gap-3 mb-2">
        {isPositive ? (
          <CheckCircle className="h-5 w-5 text-[var(--green)]" />
        ) : (
          <XCircle className="h-5 w-5 text-[var(--mustard)]" />
        )}
        <div>
          <span className="font-semibold text-[var(--text-pri)]">
            {isPositive ? term : term}
          </span>
          {!isPositive && (
            <span className="text-[var(--text-sec)] ml-2">→ {replacement}</span>
          )}
        </div>
      </div>
      <p className="text-sm text-[var(--text-sec)]">{reason}</p>
    </div>
  );

  const MicrocopyExample: React.FC<{
    context: string;
    before: string;
    after: string;
    reason: string;
  }> = ({ context, before, after, reason }) => (
    <div className="border border-[var(--rule)] bg-[var(--surface)] p-6">
      <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-4">{context}</h3>
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm font-semibold text-[var(--mustard)] mb-2">Before</p>
          <div className="bg-[var(--mustard-dim)] border border-[var(--rule)] p-3">
            <p className="text-sm text-[var(--text-sec)]">{before}</p>
          </div>
        </div>
        <div>
          <p className="text-sm font-semibold text-[var(--green)] dark:text-[var(--text-ter)] mb-2">After</p>
          <div className="bg-[var(--green-dim)] border border-[var(--rule)] p-3">
            <p className="text-sm text-[var(--text-sec)]">{after}</p>
          </div>
        </div>
      </div>
      <p className="text-sm text-[var(--text-sec)]">{reason}</p>
    </div>
  );

  return (
    <div>
      <ProductDesignPageHeader
        title="Tone & Voice System"
        eyebrow="Copy · facilitation"
        backTo="/design-language"
        backLabel="Design language"
        subtitle="Communication guidelines that ensure consistent, effective messaging across all Peak Priorities touchpoints. Our voice reflects clarity, precision, warmth, and decisiveness."
      />

      {/* System Overview */}
      <ProductDesignSection title="Voice & Tone Overview">
        <p className="text-[var(--text-sec)] leading-relaxed mb-6">
          Our communication system ensures every interaction feels authentically Peak Priorities.
          We speak with clarity and purpose, helping people navigate complexity to reach their goals.
        </p>

        <div className="grid md:grid-cols-4 gap-4">
          <div className="bg-[var(--mustard-dim)] border border-[var(--rule)] p-4">
            <h3 className="font-heading font-semibold text-[var(--text-pri)] mb-2">Clear</h3>
            <p className="text-sm text-[var(--text-sec)]">Plain language, short sentences, one idea at a time</p>
          </div>
          <div className="bg-[var(--slate-dim)] border border-[var(--rule)] p-4">
            <h3 className="font-heading font-semibold text-[var(--text-pri)] mb-2">Precise</h3>
            <p className="text-sm text-[var(--text-sec)]">Defined terms, numbers when useful, no vague superlatives</p>
          </div>
          <div className="bg-[var(--green-dim)] border border-[var(--rule)] p-4">
            <h3 className="font-heading font-semibold text-[var(--text-pri)] mb-2">Warm</h3>
            <p className="text-sm text-[var(--text-sec)]">Respectful, encouraging, never scolding</p>
          </div>
          <div className="bg-[var(--coral-dim)] border border-[var(--rule)] p-4">
            <h3 className="font-heading font-semibold text-[var(--text-pri)] mb-2">Decisive</h3>
            <p className="text-sm text-[var(--text-sec)]">Make recommendations, offer defaults, own the point of view</p>
          </div>
        </div>
      </ProductDesignSection>

      {/* Tab Navigation */}
      <div className="flex justify-center gap-4 mb-8 flex-wrap">
        <button
          onClick={() => setActiveTab('voice')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'voice'
              ? 'bg-[var(--slate)] text-white'
              : 'bg-[var(--surface-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <MessageSquare className="h-5 w-5" />
          Voice
        </button>
        <button
          onClick={() => setActiveTab('tone')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'tone'
              ? 'bg-[var(--slate)] text-white'
              : 'bg-[var(--surface-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Volume2 className="h-5 w-5" />
          Tone
        </button>
        <button
          onClick={() => setActiveTab('vocabulary')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'vocabulary'
              ? 'bg-[var(--slate)] text-white'
              : 'bg-[var(--surface-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Type className="h-5 w-5" />
          Vocabulary
        </button>
        <button
          onClick={() => setActiveTab('microcopy')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'microcopy'
              ? 'bg-[var(--slate)] text-white'
              : 'bg-[var(--surface-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Copy className="h-5 w-5" />
          Microcopy
        </button>
      </div>

      {/* Voice Attributes */}
      {activeTab === 'voice' && (
        <div>
          <ProductDesignSection title="Core Voice Attributes">
            <p className="text-[var(--text-sec)] mb-6">
              These four attributes define how Peak Priorities sounds in every communication.
              They work together to create a voice that's both professional and approachable.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <VoiceAttribute
                letter="C"
                title="Clear"
                description="We communicate with precision and purpose. Every word serves a function, and every message has a clear intent."
                example="Transform overwhelming priority lists into clear action plans"
                color="bg-[var(--mustard)]"
              />
              <VoiceAttribute
                letter="P"
                title="Precise"
                description="We choose words carefully and avoid ambiguity. Our language is specific, measurable, and actionable."
                example="Reduce decision time by 40% with our systematic framework"
                color="bg-[var(--slate)]"
              />
              <VoiceAttribute
                letter="W"
                title="Warm"
                description="We approach complex topics with empathy and understanding. Our tone is supportive, not condescending."
                example="We understand this feels overwhelming. Let's break it down into manageable steps"
                color="bg-[var(--green)]"
              />
              <VoiceAttribute
                letter="D"
                title="Decisive"
                description="We provide direction and confidence. Our communication helps people move forward with clarity and purpose."
                example="Start with these three priorities. Everything else can wait."
                color="bg-[var(--coral)]"
              />
            </div>
          </ProductDesignSection>

          {/* Messaging Pillars */}
          <ProductDesignSection title="Messaging Pillars">
            <p className="text-[var(--text-sec)] mb-6">
              These core messages should be woven throughout all our communications to reinforce our value proposition.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-[var(--mustard-dim)] border border-[var(--rule)] p-6">
                <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-3">From Fog to Focus</h3>
                <p className="text-sm text-[var(--text-sec)] mb-4">We help leaders navigate complexity to reach clarity. Every communication should reinforce this journey metaphor.</p>
                <div className="bg-[var(--surface-raised)] p-3">
                  <p className="text-sm text-[var(--text-sec)] italic">"Transform overwhelming priority lists into clear action plans"</p>
                </div>
              </div>

              <div className="bg-[var(--slate-dim)] border border-[var(--rule)] p-6">
                <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-3">Systematic Approach</h3>
                <p className="text-sm text-[var(--text-sec)] mb-4">We provide frameworks, not just advice. Emphasize the structured, repeatable nature of our methods.</p>
                <div className="bg-[var(--surface-raised)] p-3">
                  <p className="text-sm text-[var(--text-sec)] italic">"A proven framework for aligning teams and accelerating decisions"</p>
                </div>
              </div>

              <div className="bg-[var(--green-dim)] border border-[var(--rule)] p-6">
                <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-3">Evidence-Based</h3>
                <p className="text-sm text-[var(--text-sec)] mb-4">Our methods are grounded in research and real-world results. Use data and examples to build credibility.</p>
                <div className="bg-[var(--surface-raised)] p-3">
                  <p className="text-sm text-[var(--text-sec)] italic">"Backed by research with Fortune 500 companies"</p>
                </div>
              </div>
            </div>
          </ProductDesignSection>
        </div>
      )}

      {/* Tone Dial */}
      {activeTab === 'tone' && (
        <div>
          <ProductDesignSection title="Tone Dial">
            <p className="text-[var(--text-sec)] mb-6">
              Our tone adapts to context while maintaining our core voice. Use this guide to calibrate your communication for different situations.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <ToneContext
                title="Executive Context"
                when="C-suite presentations, formal reports, official communications"
                tone="High directness, medium warmth, low detail, fast tempo"
                example="Our systematic approach to prioritization enables organizations to achieve measurable outcomes through structured decision-making processes."
                color="bg-[var(--mustard)]"
              />

              <ToneContext
                title="Conversational Context"
                when="Workshops, team meetings, blog posts, social media"
                tone="High directness, high warmth, low detail, fast tempo"
                example="Let's cut through the noise and get to what really matters. Here's how we can help you make better decisions, faster."
                color="bg-[var(--slate)]"
              />

              <ToneContext
                title="Supportive Context"
                when="Coaching sessions, troubleshooting, difficult conversations"
                tone="Medium-high directness, high warmth, medium detail, medium tempo"
                example="We understand this feels overwhelming. Let's break it down into manageable steps and find a path forward together."
                color="bg-[var(--green)]"
              />
            </div>
          </ProductDesignSection>

          {/* Tone Guidelines */}
          <ProductDesignSection title="Tone Guidelines by Context">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Assessment UI & Microcopy</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--mustard-dim)] border border-[var(--rule)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Imperatives</h4>
                    <p className="text-sm text-[var(--text-sec)]">Use friendly guardrails and reassure progress</p>
                  </div>

                  <div className="bg-[var(--slate-dim)] border border-[var(--rule)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Progress Indicators</h4>
                    <p className="text-sm text-[var(--text-sec)]">"You're making great progress" vs "Complete the assessment"</p>
                  </div>

                  <div className="bg-[var(--green-dim)] border border-[var(--rule)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Error Messages</h4>
                    <p className="text-sm text-[var(--text-sec)]">"Let's try that again" vs "Invalid input"</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Workshop Materials</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--mustard-dim)] border border-[var(--rule)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Instructional Verbs</h4>
                    <p className="text-sm text-[var(--text-sec)]">Use examples and checklists for clarity</p>
                  </div>

                  <div className="bg-[var(--slate-dim)] border border-[var(--rule)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Medium Detail</h4>
                    <p className="text-sm text-[var(--text-sec)]">Provide enough context without overwhelming</p>
                  </div>

                  <div className="bg-[var(--green-dim)] border border-[var(--rule)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">High Warmth</h4>
                    <p className="text-sm text-[var(--text-sec)]">Encourage participation and learning</p>
                  </div>
                </div>
              </div>
            </div>
          </ProductDesignSection>
        </div>
      )}

      {/* Vocabulary Guide */}
      {activeTab === 'vocabulary' && (
        <div>
          <ProductDesignSection title="Vocabulary Guide">
            <p className="text-[var(--text-sec)] mb-6">
              Consistent language builds brand recognition and clarity. Use these preferred terms throughout all communications.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Preferred Terms</h3>
                <div className="space-y-4">
                  <VocabularyItem
                    term="Prioritization"
                    replacement=""
                    reason="More precise than 'prioritizing' - emphasizes the systematic process"
                    isPositive={true}
                  />
                  <VocabularyItem
                    term="Decision-making"
                    replacement=""
                    reason="Hyphenated for clarity and consistency"
                    isPositive={true}
                  />
                  <VocabularyItem
                    term="Framework"
                    replacement=""
                    reason="More specific than 'methodology' - implies structure and repeatability"
                    isPositive={true}
                  />
                  <VocabularyItem
                    term="Expedition"
                    replacement=""
                    reason="Brand-specific term that reinforces the navigation metaphor"
                    isPositive={true}
                  />
                  <VocabularyItem
                    term="Wayfinding"
                    replacement=""
                    reason="Unique term that captures our navigation expertise"
                    isPositive={true}
                  />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Terms to Avoid</h3>
                <div className="space-y-4">
                  <VocabularyItem
                    term="Best practices"
                    replacement="Proven methods"
                    reason="Too generic and overused - be more specific"
                    isPositive={false}
                  />
                  <VocabularyItem
                    term="Synergy"
                    replacement="Alignment"
                    reason="Overused buzzword - use clearer alternatives"
                    isPositive={false}
                  />
                  <VocabularyItem
                    term="Leverage"
                    replacement="Use"
                    reason="When 'use' works better, choose the simpler word"
                    isPositive={false}
                  />
                  <VocabularyItem
                    term="Circle back"
                    replacement="Follow up"
                    reason="Cliché that adds no value - be direct"
                    isPositive={false}
                  />
                  <VocabularyItem
                    term="Deep dive"
                    replacement="Detailed analysis"
                    reason="Overused phrase - be more specific about what you're doing"
                    isPositive={false}
                  />
                </div>
              </div>
            </div>
          </ProductDesignSection>

          {/* Structural Bans */}
          <ProductDesignSection title="Structural Bans">
            <p className="text-[var(--text-sec)] mb-6">
              These phrases are banned from all Peak Priorities communications. They add no value and weaken our message.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-[var(--mustard-dim)] border border-[var(--rule)] p-6">
                <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-3">Banned Phrases</h3>
                <ul className="space-y-2 text-sm text-[var(--text-sec)]">
                  <li>• "It's not just about..."</li>
                  <li>• "...aren't just about X; they're Y"</li>
                  <li>• "...isn't (just) about — it's about..."</li>
                  <li>• "Revolutionary" / "Ultimate" / "Cutting-edge"</li>
                  <li>• "Optimize" / "Streamline" (without what/why)</li>
                </ul>
              </div>

              <div className="bg-[var(--green-dim)] border border-[var(--rule)] p-6">
                <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-3">Better Alternatives</h3>
                <ul className="space-y-2 text-sm text-[var(--text-sec)]">
                  <li>• Be direct: "Our approach focuses on..."</li>
                  <li>• Use specific outcomes: "Reduce rework by 30%"</li>
                  <li>• Define terms: "Our framework for..."</li>
                  <li>• Be explicit: "Reduce decision time by 40%"</li>
                  <li>• Show results: "Backed by research with..."</li>
                </ul>
              </div>
            </div>
          </ProductDesignSection>
        </div>
      )}

      {/* Microcopy Standards */}
      {activeTab === 'microcopy' && (
        <div>
          <ProductDesignSection title="Microcopy Standards">
            <p className="text-[var(--text-sec)] mb-6">
              Small words make a big difference. These standards ensure consistency in buttons, forms, and interface elements.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Buttons & Actions</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--mustard-dim)] border border-[var(--rule)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Primary CTA</h4>
                    <p className="text-sm text-[var(--text-sec)]">"Get Started" / "Begin Assessment"</p>
                  </div>

                  <div className="bg-[var(--slate-dim)] border border-[var(--rule)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Secondary CTA</h4>
                    <p className="text-sm text-[var(--text-sec)]">"Learn More" / "View Details"</p>
                  </div>

                  <div className="bg-[var(--green-dim)] border border-[var(--rule)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Destructive Action</h4>
                    <p className="text-sm text-[var(--text-sec)]">"Remove" / "Delete" (not "Destroy")</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Form Labels</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--mustard-dim)] border border-[var(--rule)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Required Fields</h4>
                    <p className="text-sm text-[var(--text-sec)]">"Name *" (not "Name (required)")</p>
                  </div>

                  <div className="bg-[var(--slate-dim)] border border-[var(--rule)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Error Messages</h4>
                    <p className="text-sm text-[var(--text-sec)]">"Please enter a valid email address"</p>
                  </div>

                  <div className="bg-[var(--green-dim)] border border-[var(--rule)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Success Messages</h4>
                    <p className="text-sm text-[var(--text-sec)]">"Assessment completed successfully"</p>
                  </div>
                </div>
              </div>
            </div>
          </ProductDesignSection>

          {/* Before & After Examples */}
          <ProductDesignSection title="Before & After Examples">
            <p className="text-[var(--text-sec)] mb-6">
              See how our voice and tone guidelines transform generic copy into Peak Priorities communication.
            </p>

            <div className="space-y-6">
              <MicrocopyExample
                context="Website Hero Section"
                before="Our comprehensive solution leverages cutting-edge methodologies to optimize your organizational efficiency and drive transformational change through innovative prioritization strategies."
                after="We help you cut through the noise to focus on what matters most. Our systematic approach turns overwhelming priority lists into clear action plans."
                reason="Removed jargon, made it more direct and specific, focused on outcomes rather than process"
              />

              <MicrocopyExample
                context="Assessment Introduction"
                before="Please complete the following assessment to evaluate your current prioritization capabilities and identify areas for improvement."
                after="Let's discover your prioritization strengths and find opportunities to work more effectively. This assessment takes about 10 minutes."
                reason="Made it more conversational, added time expectation, focused on benefits rather than evaluation"
              />

              <MicrocopyExample
                context="Error Message"
                before="Invalid input detected. Please review your entries and try again."
                after="Let's try that again. Please check your answers and resubmit."
                reason="Made it warmer and more supportive, removed technical language, focused on next steps"
              />
            </div>
          </ProductDesignSection>
        </div>
      )}

      {/* Implementation Guidelines */}
      <div className="border border-[var(--rule)] bg-[var(--surface-raised)] p-8 text-center">
        <h3 className="text-2xl font-heading font-semibold text-[var(--text-pri)] mb-3">
          Implementation Guidelines
        </h3>
        <p className="text-[var(--text-sec)] max-w-3xl mx-auto mb-6">
          Use these voice and tone guidelines consistently across all Peak Priorities communications.
          Every word should reinforce our brand values and help people move forward with clarity.
        </p>
        <div className="flex gap-4 justify-center flex-wrap">
          <button className="flex items-center gap-2 px-6 py-3 bg-[var(--slate)] text-white font-semibold hover:bg-[var(--hover-tint)] transition-colors">
            <Download className="h-5 w-5" />
            Download Guidelines
          </button>
          <button className="flex items-center gap-2 px-6 py-3 bg-[var(--surface)] text-[var(--text-pri)] border border-[var(--rule)] font-semibold hover:bg-[var(--hover-tint)] transition-colors">
            <MessageSquare className="h-5 w-5" />
            View Examples
          </button>
        </div>
      </div>
    </div>
  );
};
